import React, { useCallback, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import kkpService from '../../services/kkpService';
import { notify } from '../../components/common/AppToast';
import { formatPlanningImportMessage } from '../../utils/kkpPlanningImport';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import KkpPlanningStatutDisplay from '../../components/kkp/KkpPlanningStatutDisplay';
import KkpVenteWebModal from '../../components/kkp/KkpVenteWebModal';
import KkpPlanningMagInlineCell from '../../components/kkp/KkpPlanningMagInlineCell';
import { filterStatutsForContext, filterStatutsForMonth } from '../../utils/kkpStatutUtils';
import { buildPlanningWeekColorByRowId } from '../../utils/kkpPlanningWeekUtils';
import KkpPlanningStickyTableWrap from '../../components/kkp/KkpPlanningStickyTableWrap';
import { AppFilterSelect, AppFilterCommercialSelect, AppFilterField, AppFilterDateRange } from '../../components/filters';
import { AppButton } from '../../components/ui';
import KkpClientWeekSpanBadge from '../../components/kkp/KkpClientWeekSpanBadge';
import {
  buildClientWeekSpanByRowKey,
  formatClientWeekSpanBadge,
} from '../../utils/kkpPlanningClientWeekSpan';
import KkpPlanningImportModal from '../../components/kkp/KkpPlanningImportModal';
import KkpPubImageButton from '../../components/kkp/KkpPubImageButton';
import KkpBatValideModal from '../../components/kkp/KkpBatValideModal';
import SortableTh from '../../components/common/SortableTh';
import KkpClientAutocomplete from '../../components/kkp/KkpClientAutocomplete';
import KkpWebConflictAlert from '../../components/kkp/KkpWebConflictAlert';
import { getPlanningRowKey, mergePlanningWeekRows } from '../../utils/kkpPlanningMergeUtils';
import { confirmVenteClear } from '../../utils/confirmVenteDelete';
import {
  buildClientInlinePayload,
  clientInlineDraftEquals,
  getClientDraftLabel,
  getClientInlineDraft,
} from '../../utils/kkpPlanningClientInline';
import { suggestBriefWizardStep, briefStatusWizardStep } from '../../utils/kkpVenteBriefUtils';
import { buildSemaineOptionsFromVentes } from '../../utils/kkpPlanningSemaineOptions';
import { filterRowsByMulti, joinOrEmpty } from '../../utils/kkpPlanningFilters';
import {
  normalizeDateFilter,
  normalizeMultiFilter,
  normalizeTextFilter,
} from '../../utils/kkpPlanningFiltersStorage';
import { usePlanningWeekAutoScroll } from '../../hooks/usePlanningWeekAutoScroll';
import {
  getPlanningClientDisplay,
  getPlanningViewEmptyMessage,
  isPlanningIndispoWithoutClient,
  PLANNING_VIEW_CLIENTS,
} from '../../utils/kkpPlanningViewMode';
import { resolveCommercialSelectionFromDolibarrClient } from '../../utils/kkpDolibarrCommercialPrefill';
import usePlanningVentesPage from '../../hooks/usePlanningVentesPage';
import KkpPlanningPageShell, { KkpPlanningEmptyRow } from '../../components/kkp/KkpPlanningPageShell';
import {
  formatAudience,
  formatTauxClick,
  parseTauxClickNumber,
  todayYmd,
  periodFromDateDebut,
  importPeriodLabelFromDateDebut,
} from '../../utils/kkpPlanningFormat';
import { computeWebMoyenneHebdo } from '../../utils/kkpPlanningWebMoyenneHebdo';

const TABLE_SORT_COLUMNS = {
  periode: { type: 'number', getValue: (row) => (row.annee ?? 0) * 100 + (row.mois ?? 0) },
  semaine: { type: 'string', getValue: (row) => row.semaine_date || row.semaine || '' },
  commercial: { type: 'string', getValue: (row) => row.commercial_nom },
  client: { type: 'string', getValue: (row) => getPlanningClientDisplay(row, '') },
  emplacement: { type: 'string', getValue: (row) => row.emplacement },
  statut: { type: 'string', getValue: (row) => row.statut_phase },
  commentaire: { type: 'string', getValue: (row) => row.commentaire },
  affichages: { type: 'number', getValue: (row) => row.affichages ?? 0 },
  clicsDesktop: {
    type: 'number',
    getValue: (row) => row.click_desktop ?? row.pub_cliquees ?? 0,
  },
  clicsMobile: { type: 'number', getValue: (row) => row.click_mobile ?? 0 },
  tauxClick: { type: 'number', getValue: (row) => parseTauxClickNumber(row.taux_click) ?? 0 },
};

const parseInlineInt = (raw) => {
  if (raw == null || raw === '') return null;
  const n = Number(String(raw).replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.round(n));
};

const parseInlineTaux = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  return trimmed.replace(/\s*%$/, '').replace(',', '.');
};

const getWebClicksDesktop = (vente) => vente.click_desktop ?? vente.pub_cliquees ?? null;

const inlineAudienceDraftEquals = (field, draft, vente) => {
  switch (field) {
    case 'affichage':
      return parseInlineInt(draft) === (vente.affichage ?? null);
    case 'click_desktop':
      return parseInlineInt(draft) === (getWebClicksDesktop(vente) ?? null);
    case 'click_mobile':
      return parseInlineInt(draft) === (vente.click_mobile ?? null);
    case 'taux_click':
      return parseInlineTaux(draft) === parseInlineTaux(vente.taux_click);
    default:
      return false;
  }
};

const getDefaultWebFilters = () => ({
  date_debut: todayYmd(),
  date_fin: '',
  commercial_id: [],
  emplacement: [],
  statut_phase: [],
  search: '',
});

const WEB_FILTERS_STORAGE_KEY = 'kkp_planning_web_filters';

const normalizeWebFilters = (savedFilters) => {
  const defaults = getDefaultWebFilters();
  const source = savedFilters && typeof savedFilters === 'object' ? savedFilters : {};

  return {
    date_debut: normalizeDateFilter(source.date_debut, defaults.date_debut),
    date_fin: normalizeDateFilter(source.date_fin, ''),
    commercial_id: normalizeMultiFilter(source.commercial_id),
    emplacement: normalizeMultiFilter(source.emplacement),
    statut_phase: normalizeMultiFilter(source.statut_phase),
    search: normalizeTextFilter(source.search, ''),
  };
};

const toWebApiFilters = (filters) => ({
  date_debut: filters.date_debut ?? '',
  date_fin: filters.date_fin ?? '',
  commercial_id: joinOrEmpty(filters.commercial_id),
  emplacement: joinOrEmpty(filters.emplacement),
  statut_phase: joinOrEmpty(filters.statut_phase),
  search: filters.search ?? '',
});

const KkpPlanningWebPage = () => {
  const [syncingWeb, setSyncingWeb] = useState(false);
  const [webWarnings, setWebWarnings] = useState([]);
  const [showWebConflictModal, setShowWebConflictModal] = useState(false);

  const fetchMeta = useCallback(() => kkpService.getWebMeta(), []);
  const initFiltersFromMeta = useCallback((_meta, savedFilters) => normalizeWebFilters(savedFilters), []);
  const fetchVentes = useCallback(async (filters, { page }) => {
    const apiFilters = toWebApiFilters(filters);
    const gridMode = Boolean(apiFilters.date_debut)
      && !apiFilters.search
      && !apiFilters.commercial_id
      && !apiFilters.statut_phase
      && (filters.commercial_id?.length ?? 0) <= 1
      && (filters.statut_phase?.length ?? 0) <= 1;
    const limit = gridMode ? 1000 : 50;
    const result = await kkpService.listVentesWeb({ ...apiFilters, page, limit });
    let nextVentes = result.ventes;
    nextVentes = filterRowsByMulti(nextVentes, filters.commercial_id, (v) => v.commercial_id);
    nextVentes = filterRowsByMulti(nextVentes, filters.emplacement, (v) => v.emplacement ?? v.format);
    nextVentes = filterRowsByMulti(nextVentes, filters.statut_phase, (v) => v.statut_phase);
    return {
      ventes: nextVentes,
      pagination: result.pagination,
      extras: { moyenneHebdo: result.moyenne_hebdo ?? null },
    };
  }, []);

  const {
    ask,
    confirmModal,
    meta,
    ventes,
    setVentes,
    commerciaux,
    defaultCommercialId,
    pagination,
    filters,
    setFilters,
    loading,
    error,
    setError,
    showModal,
    editingVente,
    modalInitialValues,
    modalWizardStep,
    openCreate,
    openEdit,
    closeModal,
    setImporting,
    setExporting,
    showImportModal,
    setShowImportModal,
    importStrategy,
    setImportStrategy,
    wipeScope,
    setWipeScope,
    fileInputRef,
    editingCell,
    setEditingCell,
    editDraft,
    setEditDraft,
    inlineSaving,
    setInlineSaving,
    batValideVente,
    batValideSubmitting,
    batValideError,
    openBatValide,
    closeBatValide,
    submitBatValide,
    statutSelectProps,
    viewMode,
    setViewMode,
    changeViewMode,
    viewModeCounts,
    visibleVentes,
    sortedRows,
    sort,
    toggleSort,
    getSortIcon,
    commercialOptions,
    loadMeta,
    loadVentes,
    findVenteByRowId,
    getRowId,
    buildImportExportMenu,
  } = usePlanningVentesPage({
    storageKey: WEB_FILTERS_STORAGE_KEY,
    tableSortColumns: TABLE_SORT_COLUMNS,
    fetchMeta,
    initFiltersFromMeta,
    fetchVentes,
  });

  const moyenneHebdo = useMemo(() => computeWebMoyenneHebdo(visibleVentes), [visibleVentes]);

  const weekColorByRowId = useMemo(
    () => buildPlanningWeekColorByRowId(ventes, getPlanningRowKey),
    [ventes],
  );

  const clientWeekSpanByRowKey = useMemo(
    () => buildClientWeekSpanByRowKey(ventes),
    [ventes],
  );

  const semaineOptions = useMemo(
    () => buildSemaineOptionsFromVentes(ventes, {
      annee: filters?.date_debut ? Number(String(filters.date_debut).slice(0, 4)) : null,
      canal: 'web',
    }),
    [ventes, filters?.date_debut],
  );

  const filterStatuts = useMemo(
    () => filterStatutsForContext(meta?.statuts, null, null),
    [meta?.statuts],
  );

  const moisOptions = useMemo(
    () => (meta?.mois ?? []).map((m) => ({ value: String(m.value), label: m.label })),
    [meta?.mois],
  );
  const emplacementOptions = useMemo(
    () => (meta?.formats ?? []).map((f) => ({ value: f.value, label: f.value })),
    [meta?.formats],
  );
  const statutOptions = useMemo(
    () => filterStatuts.map((s) => ({ value: s.value, label: s.value })),
    [filterStatuts],
  );

  const { setRowRef, getAnchorRowProps } = usePlanningWeekAutoScroll({
    ventes,
    loading,
    filters: filters ?? getDefaultWebFilters(),
    getRowKey: getPlanningRowKey,
  });

  const applyWebMutationResult = (result) => {
    if (result?.week_rows?.length) {
      setVentes((prev) => mergePlanningWeekRows(prev, result.week_rows));
    }

    if (result?.rejected) {
      setWebWarnings(result?.warnings ?? []);
      setShowWebConflictModal(true);
      return false;
    }

    setWebWarnings([]);
    return true;
  };

  const handleOpenCreate = () => {
    const start = filters?.date_debut ? new Date(`${filters.date_debut}T00:00:00`) : new Date();
    openCreate({
      annee: start.getFullYear(),
      mois: start.getMonth() + 1,
    });
  };

  const handleCreaPubChange = (venteId, pieces) => {
    setVentes((prev) => prev.map((row) => (
      row.id === venteId ? { ...row, crea_pub_pieces_jointes: pieces } : row
    )));
  };

  const handleOpenEdit = (vente, wizardStep = undefined) => {
    openEdit(vente, typeof wizardStep === 'number' ? wizardStep : suggestBriefWizardStep(vente));
  };

  const handleClear = async (venteOrId) => {
    const vente = typeof venteOrId === 'object'
      ? venteOrId
      : findVenteByRowId(venteOrId) ?? ventes.find((v) => v.id === venteOrId);
    const id = vente?.id ?? venteOrId;
    if (!id) return;
    if (!(await confirmVenteClear(ask, vente?.client_label))) return;
    try {
      const result = await kkpService.clearVenteWeb(id);
      applyWebMutationResult(result);
      if (!result?.week_rows?.length) {
        loadVentes(pagination.page || 1);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getInlineDraft = (vente, field) => {
    switch (field) {
      case 'periode':
        return { annee: vente.annee, mois: vente.mois };
      case 'semaine':
        return vente.semaine ?? '';
      case 'commercial_id':
        return vente.commercial_id != null ? String(vente.commercial_id) : '';
      case 'client_label':
        return getClientInlineDraft(vente);
      case 'emplacement':
        return vente.emplacement ?? vente.format ?? '';
      case 'statut_phase':
        return vente.statut_phase ?? '';
      case 'commentaire':
        return vente.commentaire ?? '';
      case 'affichage':
        return vente.affichage != null && vente.affichage !== '' ? String(vente.affichage) : '';
      case 'click_desktop': {
        const clicks = getWebClicksDesktop(vente);
        return clicks != null && clicks !== '' ? String(clicks) : '';
      }
      case 'click_mobile':
        return vente.click_mobile != null && vente.click_mobile !== '' ? String(vente.click_mobile) : '';
      case 'taux_click':
        return vente.taux_click != null && vente.taux_click !== '' ? String(vente.taux_click) : '';
      default:
        return '';
    }
  };

  const startInlineEdit = async (venteId, field) => {
    if (!venteId || !field) {
      setEditingCell(null);
      setEditDraft(null);
      return;
    }
    if (editingCell && (editingCell.venteId !== venteId || editingCell.field !== field)) {
      await saveInlineEdit();
    }
    const vente = findVenteByRowId(venteId);
    if (!vente) return;
    setEditingCell({ venteId, field });
    setEditDraft(getInlineDraft(vente, field));
  };

  const buildInlinePayload = (field, draft) => {
    switch (field) {
      case 'periode':
        return { annee: draft.annee, mois: draft.mois };
      case 'semaine':
        return { semaine: draft || null };
      case 'commercial_id':
        return { commercial_id: draft || null };
      case 'client_label':
        return String(getClientDraftLabel(draft)).trim() === ''
          ? {
            client_label: '',
            dolibarr_thirdparty_id: null,
            entreprise_locale_id: null,
            commercial_id: null,
            statut_phase: '',
          }
          : buildClientInlinePayload(draft);
      case 'emplacement':
        return { emplacement: draft };
      case 'statut_phase':
        return { statut_phase: draft ?? '' };
      case 'commentaire':
        return { commentaire: draft || null };
      case 'affichage':
        return { affichage: parseInlineInt(draft) };
      case 'click_desktop':
        return { click_desktop: parseInlineInt(draft) };
      case 'click_mobile':
        return { click_mobile: parseInlineInt(draft) };
      case 'taux_click':
        return { taux_click: parseInlineTaux(draft) };
      default:
        return {};
    }
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const vente = findVenteByRowId(editingCell.venteId);
    if (!vente) return;

    const draft = draftOverride !== undefined ? draftOverride : editDraft;
    const original = getInlineDraft(vente, editingCell.field);
    const unchanged = editingCell.field === 'client_label'
      ? clientInlineDraftEquals(draft, vente)
      : ['affichage', 'click_desktop', 'click_mobile', 'taux_click'].includes(editingCell.field)
        ? inlineAudienceDraftEquals(editingCell.field, draft, vente)
        : JSON.stringify(draft) === JSON.stringify(original);
    if (unchanged) {
      setEditingCell(null);
      setEditDraft(null);
      return;
    }

    setInlineSaving(true);
    setError(null);
    try {
      const payload = buildInlinePayload(editingCell.field, draft);

      let result;
      if (vente.is_placeholder) {
        const createPayload = {
          annee: vente.annee,
          mois: vente.mois,
          semaine: vente.semaine,
          emplacement: vente.emplacement ?? vente.format,
          web_slot_index: vente.web_slot_index ?? 1,
          client_label: '',
          statut_phase: '',
          ...payload,
        };
        result = await kkpService.createVenteWeb(createPayload);
      } else {
        result = await kkpService.updateVenteWeb(vente.id, payload);
      }

      applyWebMutationResult(result);

      setEditingCell(null);
      setEditDraft(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const currentPeriod = () => periodFromDateDebut(filters?.date_debut || todayYmd());

  const importPeriodLabel = useMemo(
    () => importPeriodLabelFromDateDebut(filters?.date_debut, moisOptions),
    [filters?.date_debut, moisOptions],
  );

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const { annee, mois } = currentPeriod();
      await kkpService.exportVentesWeb({ annee, mois });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleSyncWeb = async () => {
    setSyncingWeb(true);
    try {
      const { annee } = currentPeriod();
      const result = await kkpService.syncWebAudience({ annee });
      const parts = [`${result.updated} ligne(s) mise(s) à jour`];
      if (result.unmatched > 0) parts.push(`${result.unmatched} ligne(s) non matchée(s)`);
      if (result.errors > 0) parts.push(`${result.errors} erreur(s)`);
      let msg = `Sync Web : ${parts.join(', ')}.`;
      const firstUnmatched = result.unmatched_details?.[0];
      if (firstUnmatched) {
        const detail = [
          firstUnmatched.client,
          firstUnmatched.reason,
          firstUnmatched.message,
          firstUnmatched.hint,
        ]
          .filter(Boolean)
          .join(' — ');
        if (detail) {
          msg += ` Ex. : ${detail}`;
        }
      }
      const firstError = result.error_details?.[0];
      if (firstError?.message) {
        msg += ` Erreur : ${firstError.client || ''} ${firstError.message}`.trim();
      }
      if (result.errors > 0) {
        notify.warning(msg);
      } else if (result.updated > 0) {
        notify.success(msg);
      } else {
        notify.info(msg);
      }
      loadVentes(pagination.page);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSyncingWeb(false);
    }
  };

  const handleImportClick = () => {
    setImportStrategy('upsert');
    setWipeScope('year');
    setShowImportModal(true);
  };

  const handleImportConfirm = () => {
    if (importStrategy === 'wipe' && (wipeScope === 'year' || wipeScope === 'month') && !filters?.date_debut) {
      setError('Choisissez une date de début pour indiquer l’année / le mois à remplacer.');
      return;
    }
    setShowImportModal(false);
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImporting(true);
    setError(null);
    try {
      const { annee, mois } = currentPeriod();
      const result = await kkpService.importVentesWeb(file, {
        upsert: importStrategy === 'upsert',
        replace: importStrategy === 'wipe' && wipeScope === 'month',
        replaceAll: importStrategy === 'wipe' && wipeScope === 'all',
        replaceYear: importStrategy === 'wipe' && wipeScope === 'year',
        annee: importStrategy === 'wipe' && (wipeScope === 'month' || wipeScope === 'year') ? annee : '',
        mois: importStrategy === 'wipe' && wipeScope === 'month' ? mois : '',
      });
      const msg = formatPlanningImportMessage(result);
      if ((result.errors?.length ?? 0) > 0) {
        notify.warning(`${msg} ${result.errors.length} erreur(s) — voir la console.`);
        console.warn('Import WEB erreurs', result.errors);
      } else {
        notify.success(msg);
      }
      await loadMeta();
      loadVentes(1);
    } catch (err) {
      setError(err.message);
      notify.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  const resetWebFilters = () => {
    setViewMode(PLANNING_VIEW_CLIENTS);
    setFilters({
      ...getDefaultWebFilters(),
      commercial_id: [],
    });
  };

  return (
    <KkpPlanningPageShell
      title="Planning ventes WEB"
      subtitle="Suivi des ventes web à partir d'aujourd'hui, par emplacement"
      actions={[
        {
          key: 'sync',
          label: syncingWeb ? 'Sync…' : 'Stats Web',
          icon: 'mdi:web-sync',
          onClick: handleSyncWeb,
          disabled: syncingWeb,
          loading: syncingWeb,
        },
      ]}
      menuItems={buildImportExportMenu(handleImportClick, handleExport)}
      error={error}
      kpi={moyenneHebdo ? (
        <div className="card mb-24 kkp-planning-kpi-card">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <span className="kkp-planning-kpi-card__label mb-0">Moyenne hebdo</span>
              {moyenneHebdo.semaines_avec_donnees > 0 ? (
                <>
                  <span className="text-sm text-secondary-light">
                    Affichages : <strong>{formatAudience(moyenneHebdo.affichage)}</strong>
                  </span>
                  <span className="text-sm text-secondary-light">
                    Clics pub : <strong>{formatAudience(moyenneHebdo.pub_cliquees)}</strong>
                  </span>
                  <span className="text-sm text-secondary-light">
                    Taux de click : <strong>{formatTauxClick(moyenneHebdo.taux_click)}</strong>
                  </span>
                </>
              ) : (
                <span className="text-sm text-secondary-light">Aucune donnée audience sur cette période.</span>
              )}
            </div>
          </div>
        </div>
      ) : null}
      fileInputRef={fileInputRef}
      onImportFile={handleImportFile}
      resetDisabled={!filters}
      onResetFilters={resetWebFilters}
      filterChildren={(
        <>
          <AppFilterDateRange
            id="web-periode"
            label="Période d'affichage"
            col="col-12 col-md-5 col-lg-4"
            dateDebut={filters?.date_debut ?? todayYmd()}
            dateFin={filters?.date_fin ?? ''}
            onChange={({ dateDebut, dateFin }) => setFilters((f) => ({
              ...f,
              date_debut: dateDebut || todayYmd(),
              date_fin: dateFin || '',
            }))}
            disabled={!filters}
          />
          <AppFilterCommercialSelect
            id="web-commercial"
            col="col-6 col-md-2"
            value={filters?.commercial_id ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, commercial_id: next }))}
            options={commercialOptions}
            disabled={!filters}
            multiple
          />
          <AppFilterSelect
            id="web-emplacement"
            label="Emplacement"
            col="col-6 col-md-2"
            value={filters?.emplacement ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, emplacement: next }))}
            options={emplacementOptions}
            disabled={!filters}
          />
          <AppFilterSelect
            id="web-statut"
            label="Statut"
            col="col-6 col-md-2"
            value={filters?.statut_phase ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, statut_phase: next }))}
            options={statutOptions}
            disabled={!filters}
          />
          <AppFilterField col="col-12 col-md-4" label="Client">
            <KkpClientAutocomplete
              value={filters?.search ?? ''}
              onChange={(val) => setFilters((f) => ({ ...f, search: val }))}
              onSelect={async (client) => {
                const label = String(client?.nom ?? client?.label ?? '').trim();
                try {
                  const matchedCommercial = await resolveCommercialSelectionFromDolibarrClient(client, commercialOptions);
                  setFilters((f) => ({
                    ...f,
                    search: label,
                    commercial_id: matchedCommercial ? [matchedCommercial.value] : [],
                  }));
                } catch {
                  setFilters((f) => ({ ...f, search: label }));
                }
              }}
              disabled={!filters}
              size="sm"
              placeholder="Client ou tiers Dolibarr…"
            />
          </AppFilterField>
        </>
      )}
      viewMode={viewMode}
      onViewModeChange={changeViewMode}
      viewModeCounts={viewModeCounts}
      onCreate={handleOpenCreate}
      loading={loading}
      table={(
        <KkpPlanningStickyTableWrap>
          <table className="table table-hover mb-0 kkp-planning-table kkp-planning-table--web">
            <thead>
              <tr>
                <SortableTh sortKey="periode" label="Période" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="semaine" label="Semaine" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="client" label="Client" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="emplacement" label="Emplacement" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="commentaire" label="Commentaire" className="kkp-table-comment" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="affichages" label="Aff." className="text-end" title="Affichages (impressions)" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="clicsDesktop" label="Clics" className="text-end" title="Clics pub (total sync ou desktop)" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="clicsMobile" label="Mob" className="text-end" title="Clics mobile (si dispo)" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="tauxClick" label="Taux" className="text-end" title="Taux de click" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh label="Actions" className="text-end kkp-col-actions" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {visibleVentes.length === 0 ? (
                <KkpPlanningEmptyRow
                  colSpan={12}
                  message={getPlanningViewEmptyMessage(viewMode)}
                  actions={[
                    {
                      key: 'reset',
                      label: 'Réinitialiser',
                      icon: 'mdi:filter-off-outline',
                      onClick: resetWebFilters,
                    },
                    {
                      key: 'create',
                      label: 'Nouvelle vente',
                      icon: 'mdi:plus',
                      variant: 'primary',
                      onClick: handleOpenCreate,
                    },
                  ]}
                />
              ) : sortedRows.map((v) => {
                const rowKey = getPlanningRowKey(v);
                const anchorProps = getAnchorRowProps(rowKey);
                const clientWeekSpanBadge = formatClientWeekSpanBadge(
                  clientWeekSpanByRowKey.get(rowKey),
                );
                return (
                  <tr
                    key={rowKey}
                    ref={(node) => setRowRef(rowKey, node)}
                    className={[
                      v.is_placeholder ? 'table-light' : '',
                      anchorProps.className,
                    ].filter(Boolean).join(' ') || undefined}
                    style={anchorProps.style}
                  >
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="periode"
                      type="periode"
                      options={meta?.mois ?? []}
                      optionValueKey="value"
                      optionLabelKey="label"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                    >
                      <span className="text-sm">{v.mois_label} {v.annee}</span>
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="semaine"
                      type="select"
                      options={semaineOptions}
                      optionValueKey="value"
                      optionLabelKey="label"
                      allowEmpty={false}
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className={`kkp-week-semaine-cell kkp-week-semaine-cell--${weekColorByRowId.get(getPlanningRowKey(v)) ?? 0}`}
                    >
                      <span className="text-sm">{v.semaine || '—'}</span>
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="commercial_id"
                      type="select"
                      options={commerciaux}
                      optionValueKey="id"
                      optionLabelKey="nom"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                    >
                      {v.commercial_nom ? (
                        <KkpColorBadge color={v.commercial_couleur} vivid>
                          {v.commercial_nom}
                        </KkpColorBadge>
                      ) : (
                        <span className="text-secondary-light">—</span>
                      )}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="client_label"
                      type="client"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="fw-medium"
                    >
                      <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                        <span className={isPlanningIndispoWithoutClient(v) ? 'text-secondary-light fst-italic' : undefined}>
                          {getPlanningClientDisplay(v)}
                        </span>
                        <KkpClientWeekSpanBadge
                          spanBadge={clientWeekSpanBadge}
                          id={`kkp-client-week-span-${rowKey}`}
                        />
                      </span>
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="emplacement"
                      type="select"
                      allowEmpty={false}
                      options={meta?.formats ?? []}
                      optionValueKey="value"
                      optionLabelKey="value"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                    >
                      <span className="text-sm">{v.emplacement || v.format}</span>
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="statut_phase"
                      type="select"
                      options={filterStatutsForMonth(meta?.statuts, v.annee, v.mois)}
                      optionValueKey="value"
                      optionLabelKey="value"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      {...statutSelectProps(v)}
                    >
                      {v.statut_phase ? (
                        <KkpPlanningStatutDisplay
                          vente={v}
                          onBriefClick={(briefStatus) => handleOpenEdit(v, briefStatusWizardStep(v, briefStatus))}
                        />
                      ) : (
                        <span className="text-secondary-light">—</span>
                      )}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="commentaire"
                      type="text"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-sm text-secondary-light kkp-table-comment"
                    >
                      {v.commentaire || '—'}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="affichage"
                      type="number"
                      readOnly={!v.id}
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-end text-sm"
                    >
                      {formatAudience(v.affichage)}
                      {v.audience_source === 'wordpress' && (
                        <Icon icon="mdi:cloud-check" className="ms-1 text-info" title={`Sync auto${v.audience_synced_at ? ` — ${new Date(v.audience_synced_at).toLocaleString('fr-FR')}` : ''}`} />
                      )}
                      {v.audience_source === 'manual' && (
                        <Icon icon="mdi:pencil-circle-outline" className="ms-1 text-secondary-light" title="Stats saisies à la main" />
                      )}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="click_desktop"
                      type="number"
                      readOnly={!v.id}
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-end text-sm"
                    >
                      {formatAudience(getWebClicksDesktop(v))}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="click_mobile"
                      type="number"
                      readOnly={!v.id}
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-end text-sm"
                    >
                      {formatAudience(v.click_mobile)}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="taux_click"
                      type="text"
                      readOnly={!v.id}
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-end text-sm"
                    >
                      {formatTauxClick(v.taux_click)}
                    </KkpPlanningMagInlineCell>
                    <td className="text-end kkp-col-actions">
                      <div className="kkp-actions-toolbar">
                        {v.id ? (
                          <>
                            <KkpPubImageButton vente={v} onCreaPubChange={handleCreaPubChange} />
                            <AppButton
                              size="sm"
                              variant="outline-primary"
                              icon="mdi:pencil"
                              title="Modifier"
                              aria-label="Modifier"
                              onClick={() => handleOpenEdit(v)}
                            />
                            <AppButton
                              size="sm"
                              variant="outline-warning"
                              icon="mdi:eraser"
                              title="Effacer le contenu (conserve l'emplacement)"
                              aria-label="Effacer"
                              onClick={() => handleClear(v)}
                            />
                          </>
                        ) : (
                          <span className="text-secondary-light kkp-actions-hint">Double-clic pour remplir</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </KkpPlanningStickyTableWrap>
      )}
      pagination={pagination}
      onPageChange={(page) => loadVentes(page)}
      modals={(
        <>
          <KkpVenteWebModal
            show={showModal}
            onClose={closeModal}
            onSaved={(result) => {
              applyWebMutationResult(result);
            }}
            onDeleted={(result) => {
              applyWebMutationResult(result);
              setShowWebConflictModal(false);
              setWebWarnings([]);
            }}
            onError={setError}
            meta={meta}
            defaultCommercialId={defaultCommercialId}
            vente={editingVente}
            initialValues={modalInitialValues}
            initialWizardStep={modalWizardStep}
            onRequestBatValide={(nextVente) => {
              closeModal();
              openBatValide(nextVente);
            }}
          />

          <KkpBatValideModal
            show={Boolean(batValideVente)}
            vente={batValideVente}
            onClose={closeBatValide}
            onSubmit={submitBatValide}
            submitting={batValideSubmitting}
            error={batValideError}
          />

          <KkpWebConflictAlert
            show={showWebConflictModal}
            warnings={webWarnings}
            onClose={() => {
              setShowWebConflictModal(false);
              setWebWarnings([]);
            }}
          />

          <KkpPlanningImportModal
            show={showImportModal}
            title="Importer le planning WEB"
            sheetHint="La feuille VENTE WEB 2026 (ou VENTE WEB) du fichier sera lue automatiquement."
            canalLabel="WEB"
            strategy={importStrategy}
            onStrategyChange={setImportStrategy}
            wipeScope={wipeScope}
            onWipeScopeChange={setWipeScope}
            allowYearScope
            periodLabel={importPeriodLabel}
            onClose={() => setShowImportModal(false)}
            onConfirm={handleImportConfirm}
          />
          {confirmModal}
        </>
      )}
    />
  );
};

export default KkpPlanningWebPage;
